import { useMemo, useRef, useState } from 'react';
import {
  newSubmissionId,
  signUpload,
  uploadToCloudinary,
  verifyMultiPhoto,
} from '../lib/api.js';
import { enqueue, isOnline } from '../lib/submissionQueue.js';
import { compressForUpload, shouldCompress } from '../lib/imageCompression.js';

// Multi-photo challenges (e.g., the Auke Bay quest) want N photos
// submitted as a SET. Each slot uploads independently to Cloudinary so
// the player can re-take any one before committing. Only when every
// slot has a URL do we POST to /api/photo/multi-verify.
//
// iOS multi-select: each slot's `<input multiple>` lets the player
// pick up to N photos in one picker invocation. Files past the first
// fill the slots AFTER the tapped one, in order. Selecting more than
// fits is silently truncated.

export default function MultiPhotoSubmission({
  tripId,
  playerId,
  challengeId,
  testMode,
  onVerified,
  required = 3,
}) {
  // Regenerate the submission_id whenever the challenge changes so each
  // multi-photo submission is uniquely keyed even within one mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const submissionId = useMemo(() => newSubmissionId(), [challengeId]);
  const [slots, setSlots] = useState(() => Array.from({ length: required }, () => emptySlot()));
  const [verifying, setVerifying] = useState(false);
  const [queued, setQueued] = useState(false);
  const [error, setError] = useState(null);

  const allUploaded = slots.every((s) => s.url);
  const allHaveFile = slots.every((s) => s.file || s.url);

  function updateSlot(i, patch) {
    setSlots((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }

  // Distribute a multi-pick across the tapped slot and the ones after it.
  async function handleFiles(startIndex, files) {
    if (!files || files.length === 0) return;
    setError(null);
    const limit = Math.min(files.length, required - startIndex);
    // Process sequentially so iOS Safari doesn't blow memory on multiple
    // 30 MB JPEGs decoding at once.
    for (let offset = 0; offset < limit; offset += 1) {
      const slotIndex = startIndex + offset;
      // eslint-disable-next-line no-await-in-loop
      await handleFile(slotIndex, files[offset]);
    }
  }

  async function handleFile(i, rawFile) {
    if (!rawFile) return;
    setError(null);
    const slotSubmissionId = `${submissionId}_p${i + 1}`;
    updateSlot(i, {
      stage: 'compressing',
      progress: 0,
      previewUrl: URL.createObjectURL(rawFile),
      url: null,
      file: rawFile,
    });

    // Compression first — Cloudinary free tier 10 MB ceiling. EXIF preserved.
    let file = rawFile;
    if (shouldCompress(rawFile)) {
      file = await compressForUpload(rawFile);
      updateSlot(i, { file });
    }

    if (!isOnline()) {
      // Hold the (compressed) file in state so "submit set" can enqueue.
      updateSlot(i, { stage: 'held' });
      return;
    }

    try {
      updateSlot(i, { stage: 'signing' });
      const signed = await signUpload({
        tripId,
        challengeId,
        submissionId: slotSubmissionId,
      });
      updateSlot(i, { stage: 'uploading' });
      const uploaded = await uploadToCloudinary({
        file,
        signed,
        onProgress: (p) => updateSlot(i, { progress: p }),
      });
      updateSlot(i, {
        stage: 'done',
        url: uploaded.secure_url,
        exif: uploaded.image_metadata || null,
        progress: 1,
      });
    } catch (err) {
      // Network failure → hold the blob, mark slot for queue rather than
      // surfacing a hard error.
      if (!isOnline() || /failed to fetch|network|offline/i.test(err.message || '')) {
        updateSlot(i, { stage: 'held', error: null });
      } else {
        updateSlot(i, { stage: 'error', error: err.message || String(err) });
      }
    }
  }

  async function queueOffline() {
    await enqueue({
      trip_id: tripId,
      player_id: playerId,
      challenge_id: challengeId,
      submission_type: 'multi_photo',
      files: slots.map((s, i) => ({
        name: s.file?.name || `photo_${i + 1}.jpg`,
        type: s.file?.type || 'image/jpeg',
        blob: s.file,
      })),
      timestamp_claimed: new Date().toISOString(),
      test_mode: !!testMode,
    });
    setQueued(true);
  }

  async function submit() {
    if (verifying || queued) return;
    setError(null);
    setVerifying(true);

    // Path 1: any slot is held offline → queue the whole set with blobs.
    const anyHeld = slots.some((s) => s.stage === 'held' && s.file);
    if (anyHeld || !isOnline()) {
      if (!allHaveFile) {
        setError('Add a photo to every slot before submitting.');
        setVerifying(false);
        return;
      }
      try {
        await queueOffline();
      } catch (err) {
        setError(err.message || String(err));
      } finally {
        setVerifying(false);
      }
      return;
    }

    // Path 2: all slots uploaded — run verifier; on net failure, queue.
    if (!allUploaded) {
      setError('Wait for uploads to finish.');
      setVerifying(false);
      return;
    }
    try {
      const result = await verifyMultiPhoto({
        tripId,
        challengeId,
        submissionId,
        photoUrls: slots.map((s) => s.url),
        timestamp: new Date().toISOString(),
        testMode,
        exifList: slots.map((s) => s.exif || null),
      });
      onVerified?.(result);
    } catch (err) {
      if (!isOnline() || /failed to fetch|network|offline/i.test(err.message || '')) {
        try {
          await queueOffline();
          return;
        } catch (qerr) {
          setError(qerr.message || String(qerr));
        }
      } else {
        setError(err.message || String(err));
      }
    } finally {
      setVerifying(false);
    }
  }

  return (
    <div className="space-y-3">
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {required}-photo submission · {slots.filter((s) => s.url).length}/{required} uploaded
      </p>
      <div className="grid grid-cols-3 gap-2">
        {slots.map((slot, i) => (
          <Slot
            key={i}
            index={i}
            slot={slot}
            onFiles={(files) => handleFiles(i, files)}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={submit}
        disabled={(!allUploaded && !allHaveFile) || verifying || queued}
        className="flex h-12 w-full items-center justify-center rounded-md border border-action bg-action font-mono text-xs uppercase tracking-wider text-action-fg transition hover:bg-action-hover disabled:opacity-60"
      >
        {queued
          ? 'queued · drains when online'
          : verifying
            ? 'awaiting verdict…'
            : allUploaded
              ? 'submit set'
              : allHaveFile
                ? 'submit set (offline · queue)'
                : `add ${required - slots.filter((s) => s.url || s.file).length} more`}
      </button>

      {queued && (
        <p className="rounded-md border border-copper/40 bg-copper/10 px-3 py-2 font-mono text-[11px] text-copper-dark">
          Held offline. The Wilman tab&apos;s queue will drain when signal returns.
        </p>
      )}
      {error && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

function emptySlot() {
  return { stage: 'idle', url: null, previewUrl: null, progress: 0, error: null, file: null, exif: null };
}

function Slot({ index, slot, onFiles }) {
  const inputRef = useRef(null);
  const busy = slot.stage === 'compressing' || slot.stage === 'signing' || slot.stage === 'uploading';
  return (
    <div className="space-y-1">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        onChange={(e) => {
          const list = Array.from(e.target.files || []);
          if (list.length > 0) onFiles(list);
          // Clear the value so re-selecting the same file fires onChange.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="relative aspect-square w-full overflow-hidden rounded-md border border-border-tertiary bg-bg-secondary transition hover:bg-bg-tertiary disabled:opacity-60"
      >
        {slot.previewUrl ? (
          <img src={slot.previewUrl} alt="" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center font-mono text-xs text-fg-tertiary">
            #{index + 1}
          </div>
        )}
        {busy && (
          <div className="absolute inset-x-0 bottom-0 h-1 bg-bg-tertiary">
            <div
              className="h-full bg-action transition-[width]"
              style={{ width: `${(slot.progress * 100).toFixed(0)}%` }}
            />
          </div>
        )}
        {slot.stage === 'done' && (
          <span className="absolute right-1 top-1 rounded-full bg-success/90 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-action-fg">
            ok
          </span>
        )}
      </button>
      {slot.error && (
        <p className="font-mono text-[10px] text-danger">{slot.error}</p>
      )}
    </div>
  );
}
